import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { isEthereumAddress, getPkhDIDFromAddress } from 'vess-kit-web'
import { useDIDAccount } from './useDIDAccount'
import { useToast } from './useToast'
import { useVESSLoading } from './useVESSLoading'
import { useVESSMember } from './useVESSMember'
import { useWorkspace } from './useWorkspace'
import { CredItemRequest, CredType, OBCredItemRequest } from '@/@types/credItem'
import { CredentialsResponse, DataFormat } from '@/@types/credential'
import {
  CreateKmsOrganizationType,
  IssueCollectionRequest,
  MemberWithRoles,
  UpdateOrganizationType,
} from '@/@types/kms'
import {
  ADD_ADMIN_FAILED,
  ADD_ADMIN_SUCCEED,
  ORGANIZATION_CREATION_FAILED,
  ORGANIZATION_CREATION_FAILED_PLS_TRY_AGAIN,
  ORGANIZATION_CREATION_SUCCEED,
  UNAUTHORIZED,
  VC_CREATION_FAILED,
  VC_CREATION_SUCCEED,
  VC_UPDATE_FAILED,
  VC_UPDATE_SUCCEED,
} from '@/constants/toastMessage'
import { DEFAULT_LOADING_MESSAGE } from '@/constants/ui'
import {
  CredentialWithHolder,
  VSCredentialItemFromBuckup,
  addMembers,
  createCollection,
  createCredentialItem,
  createOBCredentialItem,
  createKmsOrganizations,
  deleteCredItems,
  deleteMember,
  getAllCollections,
  getCollection,
  getCredentialItem,
  getCredentialsByIssuer,
  getDefaultCollections,
  getKmsSession,
  getMembers,
  issueVerifiableCredentials,
  setRole,
  updateOrganization,
  updateVerifiableCredentials,
  getOBCredentialItem,
  OBCredentialItemFromBackup,
} from '@/lib/kms'
import { isGoodResponse, isUnAuthorized } from '@/utils/http'
import { getCurrentDomain } from '@/utils/url'

export interface SubjectUniqueInput {
  id: string
  [key: string]: any
}
export interface IssueCredentialRequest {
  issuerAddress: string
  credTypeName: string
  commonContent: any
  holders: SubjectUniqueInput[]
  credentialItemId: string
  expirationDate?: string
  notifyHolder?: boolean
}

export interface UpdateCredentialRequest {
  issuerAddress: string
  credTypeName: string
  subjects: any[]
  credentialItemId: string
  expirationDate?: string
  notifyHolder?: boolean
}

export interface saveCredentialToDBResponse {
  vc: any
}

export interface CredentialSubjectProp {
  subject: any
  existId?: string
}

export const useVESSApi = () => {
  const { showLoading, closeLoading } = useVESSLoading()
  const { showToast } = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { workspace } = useWorkspace()
  const { member } = useVESSMember(workspace?.id)
  const { originalAddress } = useDIDAccount()

  const { mutateAsync: createKmsOrganization, isLoading: isCreatingKmsOrganization } = useMutation<
    Response,
    unknown,
    CreateKmsOrganizationType
  >((param) => createKmsOrganizations(param), {
    onMutate() {
      showLoading(DEFAULT_LOADING_MESSAGE)
    },
    async onSuccess(res) {
      if (isGoodResponse(res.status)) {
        closeLoading()
        showToast(ORGANIZATION_CREATION_SUCCEED)
      } else if (isUnAuthorized(res.status)) {
        closeLoading()
        showToast(UNAUTHORIZED)
        console.log('error', res)
        router.push(`/api/auth/logout?returnTo=${getCurrentDomain()}/login`)
      } else {
        closeLoading()
        console.log('error', res)
        showToast(ORGANIZATION_CREATION_FAILED_PLS_TRY_AGAIN)
      }
    },
    onError(error) {
      console.error('error', error)
      closeLoading()
      showToast(ORGANIZATION_CREATION_FAILED)
      throw error
    },
    onSettled: () => {
      queryClient.invalidateQueries(['kmsOrganizations'])
    },
  })

  const getSessionFromKMS = async (id: string): Promise<string> => {
    try {
      const res = await getKmsSession(id, member?.id)
      if (isGoodResponse(res.status)) {
        const json = await res.json()
        return json.message as string
      } else if (isUnAuthorized(res.status)) {
        showToast(UNAUTHORIZED)
        console.log('error', res)
      } else {
        console.log('error', res)
      }
      throw new Error('Failed to get session')
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const addCollection = async (param: IssueCollectionRequest) => {
    try {
      if (!member?.id || !workspace?.id) {
        throw new Error('Failed to get member or organization')
      }
      return await createCollection(workspace?.id, member.id, param)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const fetchCredItem = async (id?: string) => {
    if (!id) {
      return null
    }
    try {
      const item = await getCredentialItem(id, true, workspace?.id, member?.id)
      return item
    } catch (error) {
      throw error
    }
  }

  const fetchOBCredItem = async (id?: string) => {
    if (!id) {
      return null
    }
    try {
      const item = await getOBCredentialItem(id, true, workspace?.id, member?.id)
      return item
    } catch (error) {
      throw error
    }
  }

  const addCredItem = async (param: CredItemRequest) => {
    try {
      if (!member?.id || !workspace?.id) {
        throw new Error('Failed to get member or organization')
      }
      return await createCredentialItem(workspace?.id, member.id, param)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const addOBCredItem = async (param: OBCredItemRequest) => {
    try {
      if (!member?.id || !workspace?.id) {
        throw new Error('Failed to get member or organization')
      }
      return await createOBCredentialItem(workspace?.id, member.id, param)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const deleteCredentialItem = async (credItemId: string) => {
    try {
      if (!member?.id || !workspace?.id) {
        throw new Error('Failed to get member or organization')
      }
      return await deleteCredItems(workspace?.id, member.id, credItemId)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const updateWorkspace = async (body: UpdateOrganizationType) => {
    try {
      if (!member?.id || !workspace?.id) {
        throw new Error('Failed to get member or organization')
      }
      return await updateOrganization(workspace?.id, member.id, body)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const addAdminMembers = async (id: string, email: string, role: string): Promise<void> => {
    try {
      if (!member?.id) {
        throw new Error('Failed to get member')
      }
      const res = await addMembers(id, member.id, email, role)
      if (isGoodResponse(res.status)) {
        showToast(ADD_ADMIN_SUCCEED)
        queryClient.invalidateQueries(['getAdminMembers', id])
        return
      } else if (isUnAuthorized(res.status)) {
        showToast(UNAUTHORIZED)
        throw new Error('Failed to add admin')
      } else {
        console.log('error', res)
      }
      showToast(ADD_ADMIN_FAILED)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const fetchAllCollection = async (id?: string) => {
    if (!id) {
      return []
    }
    try {
      return await getAllCollections(id, member?.id)
    } catch (error) {
      throw error
    }
  }

  const issue = async (
    type: CredType,
    item: any,
    data: DataFormat[],
    expirationDate?: Date,
  ): Promise<boolean> => {
    console.log({ originalAddress, workspace })
    if (!originalAddress || !workspace) {
      return false
    }
    showLoading(DEFAULT_LOADING_MESSAGE)
    try {
      console.log({ type })
      console.log({ item })

      let commonContent
      switch (type) {
        case 'attendance':
          const attendanceItem = item as VSCredentialItemFromBuckup
          commonContent = {
            eventId: attendanceItem.id,
            eventName: attendanceItem.title || '',
            eventIcon: attendanceItem.image || '',
            startDate: attendanceItem.startDate ? attendanceItem.startDate : '',
            endDate: attendanceItem.endDate ? attendanceItem.endDate : '',
            sticker:
              attendanceItem.sticker && attendanceItem.sticker.length > 0
                ? attendanceItem.sticker.map((s) => s.image)
                : undefined,
          }
          break
        case 'membership':
          const membershipItem = item as VSCredentialItemFromBuckup
          commonContent = {
            organizationName: workspace.name,
            organizationId: workspace.id || '',
            organizationIcon: workspace.icon || '',
            membershipName: membershipItem.title,
            membershipIcon: membershipItem.image,
            startDate: membershipItem.startDate ? membershipItem.startDate : '',
            endDate: membershipItem.endDate ? membershipItem.endDate : '',
            sticker:
              membershipItem.sticker && membershipItem.sticker.length > 0
                ? membershipItem.sticker.map((s) => s.image)
                : undefined,
          }
          break
        case 'certificate':
          const certificateItem = item as VSCredentialItemFromBuckup
          commonContent = {
            certificationId: certificateItem.id,
            certificationName: certificateItem.title,
            image: item.image || '',
            startDate: certificateItem.startDate ? certificateItem.startDate : '',
            endDate: certificateItem.endDate ? certificateItem.endDate : '',
            sticker:
              certificateItem.sticker && certificateItem.sticker.length > 0
                ? certificateItem.sticker.map((s) => s.image)
                : undefined,
          }
          break
        case 'openbadge':
          const openbadgeItem = item as OBCredentialItemFromBackup
          commonContent = {
            name: openbadgeItem.name,
            description: openbadgeItem.description,
            criteria: openbadgeItem.criteria,
            bakedImage: openbadgeItem.bakedImage,
            image: openbadgeItem.image,
            achievementType: openbadgeItem.achievementType,
            activityStartDate: openbadgeItem.activityStartDate,
            activityEndDate: openbadgeItem.activityEndDate,
            validFrom: openbadgeItem.validFrom,
            validUntil: openbadgeItem.validUntil,
          }
          break
        default:
          break
      }

      const subjectUniqueInputs = data.map((d) => {
        const { did, email, ...otherInfo } = d
        const formatedDid = isEthereumAddress(did) ? getPkhDIDFromAddress(did) : did.toLowerCase()
        return {
          id: formatedDid,
          ...otherInfo,
        }
      })
      const body: IssueCredentialRequest = {
        issuerAddress: originalAddress,
        credTypeName: type,
        commonContent: commonContent,
        holders: subjectUniqueInputs,
        credentialItemId: item.id,
        expirationDate: expirationDate ? expirationDate?.toISOString() : undefined,
        notifyHolder: true,
      }
      const res = await issueVerifiableCredentials(body, workspace.id, member?.id)
      if (!res) {
        throw new Error('res is undefined')
      }
      if (!isGoodResponse(res.status)) {
        throw new Error(`Failed to issue credential: ${res.statusText}`)
      }
      const resJson = await res.json()
      console.log({ resJson })
      const vcs = resJson.data as saveCredentialToDBResponse[]
      console.log({ vcs })
      queryClient.invalidateQueries(['credItem', item.id])
      queryClient.invalidateQueries(['obCredItem', item.id])
      if (vcs && vcs.length > 0) {
        closeLoading()
        showToast(VC_CREATION_SUCCEED)
        return true
      }
      closeLoading()
      showToast(VC_CREATION_FAILED)
      return false
    } catch (error) {
      console.error('error', error)
      closeLoading()
      showToast(VC_CREATION_FAILED)
      throw error
    }
  }

  const update = async (
    credential: CredentialWithHolder,
    item: VSCredentialItemFromBuckup,
    expirationDate?: Date,
  ): Promise<boolean> => {
    console.log({ originalAddress, workspace })
    if (!originalAddress || !workspace) {
      return false
    }
    showLoading(DEFAULT_LOADING_MESSAGE)
    try {
      console.log({ credential })

      const cred = JSON.parse(credential.plainCredential)
      const subjects: CredentialSubjectProp = {
        subject: cred.credentialSubject,
        existId: credential.id,
      }
      const body: UpdateCredentialRequest = {
        issuerAddress: originalAddress,
        credTypeName: item.credentialType.name as CredType,
        subjects: [subjects],
        credentialItemId: item.id,
        expirationDate: expirationDate ? expirationDate?.toISOString() : undefined,
        notifyHolder: true,
      }
      const res = await updateVerifiableCredentials(body, workspace.id, member?.id)
      if (!res) {
        throw new Error('res is undefined')
      }
      const resJson = await res.json()
      console.log({ resJson })
      const vcs = resJson.data as saveCredentialToDBResponse[]
      console.log({ vcs })
      queryClient.invalidateQueries(['credItem', item.id])
      if (vcs && vcs.length > 0) {
        closeLoading()
        showToast(VC_UPDATE_SUCCEED)
        return true
      }
      closeLoading()
      showToast(VC_UPDATE_FAILED)
      return false
    } catch (error) {
      console.error('error', error)
      closeLoading()
      showToast(VC_UPDATE_FAILED)
      throw error
    }
  }

  const updateOB = async (
    credential: CredentialWithHolder,
    item: OBCredentialItemFromBackup,
    expirationDate?: Date,
  ): Promise<boolean> => {
    console.log({ originalAddress, workspace })
    if (!originalAddress || !workspace) {
      return false
    }
    showLoading(DEFAULT_LOADING_MESSAGE)
    try {
      console.log({ credential })

      const cred = JSON.parse(credential.plainCredential)
      const subjects: CredentialSubjectProp = {
        subject: {
          name: cred.credentialSubject.achievement.name,
          description: cred.credentialSubject.description,
          criteria: cred.credentialSubject.achievement.criteria.narrative,
          image: cred.credentialSubject.achievement.image,
          bakedImage: cred.credentialSubject.image,
          achievementType: cred.credentialSubject.achievement.type,
          activityStartDate: cred.credentialSubject.activityStartDate,
          activityEndDate: cred.credentialSubject.activityEndDate,
          validFrom: cred.credentialSubject.validFrom,
          validUntil: cred.credentialSubject.validUntil,
        },
        existId: credential.id,
      }
      console.log('subjects', subjects)
      const body: UpdateCredentialRequest = {
        issuerAddress: originalAddress,
        credTypeName: 'openbadge' as CredType,
        subjects: [subjects],
        credentialItemId: item.id,
        expirationDate: expirationDate ? expirationDate?.toISOString() : undefined,
        notifyHolder: true,
      }
      const res = await updateVerifiableCredentials(body, workspace.id, member?.id)
      if (!res) {
        throw new Error('res is undefined')
      }
      const resJson = await res.json()
      console.log({ resJson })
      const vcs = resJson.data as saveCredentialToDBResponse[]
      console.log({ vcs })
      queryClient.invalidateQueries(['obCredItem', item.id])
      if (vcs && vcs.length > 0) {
        closeLoading()
        showToast(VC_UPDATE_SUCCEED)
        return true
      }
      closeLoading()
      showToast(VC_UPDATE_FAILED)
      return false
    } catch (error) {
      console.error('error', error)
      closeLoading()
      showToast(VC_UPDATE_FAILED)
      throw error
    }
  }

  const getAdminMembers = async (id?: string): Promise<MemberWithRoles[]> => {
    try {
      if (!member?.id) {
        throw new Error('Failed to get member')
      }
      if (!id) return []
      let result: MemberWithRoles[] = []
      const res = await getMembers(id, member.id)
      if (isGoodResponse(res.status)) {
        result = (await res.json()) as MemberWithRoles[]
      } else if (isUnAuthorized(res.status)) {
        showToast(UNAUTHORIZED)
        console.log('error', res)
      } else {
        console.log('error', res)
      }
      return result
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const setMemberRole = async (targetMemberId: string, role: string) => {
    if (!workspace?.id || !member?.id) {
      throw new Error('Failed to get member or organization')
    }
    try {
      console.log({ targetMemberId, role })
      return await setRole(workspace.id, member.id, targetMemberId, role)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const deleteMemberRole = async (targetMemberId: string) => {
    if (!workspace?.id || !member?.id) {
      throw new Error('Failed to get member or organization')
    }
    try {
      return await deleteMember(workspace.id, member?.id, targetMemberId)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const getCredentials = async (did?: string): Promise<CredentialsResponse | null> => {
    if (!did) return null
    const res = await getCredentialsByIssuer(did, workspace?.id, member?.id)
    return (await res.json()) as CredentialsResponse
  }

  const fetchCollection = async (id?: string) => {
    if (!id || !workspace?.id || !member?.id) {
      return null
    }
    try {
      return await getCollection(workspace.id, member.id, id)
    } catch (error) {
      throw error
    }
  }

  const fetchDefaultCredItem = async (id?: string) => {
    if (!id) {
      return null
    }
    try {
      return await getDefaultCollections(id, member?.id)
    } catch (error) {
      throw error
    }
  }

  const { data: admins, isInitialLoading: isLoadingAdmins } = useQuery<MemberWithRoles[]>(
    ['getAdminMembers', workspace?.id],
    () => getAdminMembers(workspace?.id),
    {
      enabled: !!workspace?.id && !!member?.id,
      staleTime: Infinity,
      cacheTime: 300000,
    },
  )

  return {
    createKmsOrganization,
    isCreatingKmsOrganization,
    getSessionFromKMS,
    addAdminMembers,
    admins,
    isLoadingAdmins,
    issue,
    update,
    updateOB,
    getCredentials,
    fetchCollection,
    fetchDefaultCredItem,
    fetchAllCollection,
    addCollection,
    addCredItem,
    addOBCredItem,
    fetchCredItem,
    fetchOBCredItem,
    deleteCredentialItem,
    updateWorkspace,
    setMemberRole,
    deleteMemberRole,
  }
}
